import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, OverlayView, Marker, useJsApiLoader } from '@react-google-maps/api';
import { MyLocationIcon } from '../assets/constants/Icons';
import { useLocation } from "react-router-dom";
import LockerIcon from '../assets/locker.svg';
import { applyBackendLanguageConfig, buildAddress, calculateDistanceMap, checkIfAssignmentIsProperlyConfigured, getLockerSelectionKey, getPathForNextPage, getSSize24HrPrice, setDomainAndLockerBankIdInLocalStorage, sortByDistance } from '../lib/Utils';
import { getTenantAndAssignmentSettings } from '../api/api';
import {useHistory} from 'react-router-dom';
import { LOCAL_STORAGE, LOCKER_BANK_ADDRESS, LOCKER_SLUG } from '../assets/constants/BrowserStorageKeys';
import { SUCCESS } from '../assets/constants/Constants';
import { storeSecureItemInSpecifiedStorage } from '../lib/BrowserStorageAccessMiddleware';
import { t } from 'i18next';
import CircularProgressLoader from './CircularProgressLoader';
import SearchSection from './SearchSectionComponent';
import LocationSlider from './LocationSliderComponent';
import { LOCKER_DETAILS, MAP } from '../assets/constants/PageList';
import ShowToast from './ToastComponent';
import { fetchNearbyLockersForLocation, getNearbyLockerErrorMessage } from '../lib/NearbyLockerUtils';
import { TOAST_ERROR } from '../assets/constants/Constants';

const GOOGLE_MAP_LIBRARIES = ['places'];


const transformAPIData = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) return [];
  
  return apiData.map((item, index) => {

    const lockerNumber = `${String(item.id || index + 1).padStart(3, '0')}`;

    return {
      id: item.id || index + 1,
      name: item.name || item.location || '',
      position: { 
        lat: item.latitude ? parseFloat(item.latitude) : 18.5204,
        lng: item.longitude ? parseFloat(item.longitude) : 73.8567
      },
      type: item.category?.name?.toLowerCase() || '',
      lockerNumber: lockerNumber,
      available: item.available || (item.status === 'published' ? 10 : 5),
      city: item.city || '',
      code : item.code,
      domain: item?.tenant_domain|| '',
      lockerBankId: item?.locker_bank_id || null,
      slug: item?.slug || null,
      LockerBankType: item?.bank_type || null,

    };
  });
};

const prepareLockerLocations = (apiData, referenceLocation = null) => {
  const processedData = transformAPIData(apiData);

  const validLocations = processedData.filter(
    (item) =>
      item &&
      item.position &&
      typeof item.position.lat === 'number' &&
      typeof item.position.lng === 'number' &&
      !isNaN(item.position.lat) &&
      !isNaN(item.position.lng)
  );

  if (!referenceLocation) {
    return validLocations;
  }

  const locationsWithDistance = validLocations.map((location) => ({
    ...location,
    distance: calculateDistanceMap(referenceLocation, location.position),
  }));

  return sortByDistance(locationsWithDistance, referenceLocation);
};

const LockerMapMobile = ({ isPreview = false, initialLocation = null }) => {
  const location = useLocation();
  const [dataLoaded, setDataLoaded] = useState(false);
  const { isLoaded: isMapLoaded, loadError } = useJsApiLoader({
    id: 'durolt-google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAP_LIBRARIES,
  });
  
  const { center, lockersData: passedData, timestamp } = location.state || {};
  

  const [validData, setValidData] = useState([]);
  const history = useHistory();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [zoom, setZoom] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [isSliderVisible, setIsSliderVisible] = useState(true);
  const [dragStartY, setDragStartY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [sliderHeight, setSliderHeight] = useState(35);
  
  const [currentLocation, setCurrentLocation] = useState(center || null);
  const [locationError, setLocationError] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const latestPlaceSelectionRequestRef = useRef(0);
  const [mapCenter, setMapCenter] = useState(
    center || initialLocation || { lat: 18.5204, lng: 73.8567 }
  );
  const [ isLoading, setIsLoading] = useState(false);
  const [loadingMessage, SetLoadingMessage] = useState(t('pleaseWaitText'))
  const [cachedData, setCachedData] = useState(null);
  const [liveLockersData, setLiveLockersData] = useState(null);
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const autocompleteServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const currentLocationLat = currentLocation?.lat;
  const currentLocationLng = currentLocation?.lng;
  const selectedLocationKey = getLockerSelectionKey(selectedLocation);

  useEffect(() => {
    if (!isMapLoaded || !window.google?.maps?.places) return;

    autocompleteServiceRef.current =
      autocompleteServiceRef.current || new window.google.maps.places.AutocompleteService();
    geocoderRef.current =
      geocoderRef.current || new window.google.maps.Geocoder();
  }, [isMapLoaded]);

  useEffect(() => {
    if (!isMapLoaded || !autocompleteServiceRef.current) return;

    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      setPlaceSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: trimmedQuery,
        },
        (predictions = [], status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions.length) {
            setPlaceSuggestions([]);
            return;
          }

          setPlaceSuggestions(
            predictions.slice(0, 8).map((prediction) => ({
              id: prediction.place_id,
              placeId: prediction.place_id,
              text: prediction.structured_formatting?.main_text || prediction.description,
              description:
                prediction.structured_formatting?.secondary_text || prediction.description,
              fullText: prediction.description,
              type: 'google_place',
            }))
          );
        }
      );
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [isMapLoaded, searchQuery]);

  const handlePlaceSuggestionSelect = (suggestion) => {
    if (!suggestion?.placeId || !geocoderRef.current) return;

    const requestId = latestPlaceSelectionRequestRef.current + 1;
    latestPlaceSelectionRequestRef.current = requestId;

    setSearchQuery(suggestion.fullText || suggestion.text);
    setPlaceSuggestions([]);
    setSelectedLocation(null);
    setShowInfoPanel(false);
    setIsLoading(true);
    SetLoadingMessage(t('info_message_getting_lockers_near_you'));

    geocoderRef.current.geocode({ placeId: suggestion.placeId }, async (results, status) => {
      if (requestId !== latestPlaceSelectionRequestRef.current) return;

      if (status !== 'OK' || !results?.length) {
        console.error('Failed to fetch place coordinates:', status, suggestion);
        setIsLoading(false);
        return;
      }

      const geometryLocation = results[0]?.geometry?.location;

      if (!geometryLocation) {
        console.error('No geometry found for selected place:', suggestion);
        setIsLoading(false);
        return;
      }

      const lat = geometryLocation.lat();
      const lng = geometryLocation.lng();
      const selectedPlaceLocation = { lat, lng };

      try {
        const { lockersData: apiData } = await fetchNearbyLockersForLocation({
          userLocation: selectedPlaceLocation,
          onLoadingMessage: SetLoadingMessage,
        });

        if (requestId !== latestPlaceSelectionRequestRef.current) return;

        const nextLockerData = Array.isArray(apiData) ? [...apiData] : [];
        const preparedLocations = prepareLockerLocations(nextLockerData, selectedPlaceLocation);

        setCurrentLocation(selectedPlaceLocation);
        setMapCenter(selectedPlaceLocation);
        setZoom(15);
        setValidData(preparedLocations);
        setFilteredLocations(preparedLocations);
        setLiveLockersData(nextLockerData);
        setCachedData(nextLockerData);
      } catch (error) {
        if (requestId !== latestPlaceSelectionRequestRef.current) return;
        console.error('Failed to fetch nearby lockers for selected place:', error);
        ShowToast(getNearbyLockerErrorMessage(error), TOAST_ERROR);
      } finally {
        if (requestId === latestPlaceSelectionRequestRef.current) {
          setIsLoading(false);
        }
      }
    });
  };
  
  // Cache data when received
  useEffect(() => {
    if (liveLockersData !== null) {
      localStorage.setItem('cached_locker_data', JSON.stringify({
        data: liveLockersData,
        timestamp: Date.now(),
        center: currentLocation || mapCenter
      }));
      setCachedData(liveLockersData);
    } else if (passedData && passedData.length > 0) {
      localStorage.setItem('cached_locker_data', JSON.stringify({
        data: passedData,
        timestamp: Date.now(),
        center: center
      }));
      setCachedData(passedData);
    } else if (!passedData && !cachedData) {
      const cached = localStorage.getItem('cached_locker_data');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
          setCachedData(parsed.data);
          if (parsed.center && !center) {
            setMapCenter(parsed.center);
            setCurrentLocation(parsed.center);
          }
        }
      }
    }
  }, [passedData, liveLockersData, currentLocation, mapCenter, center, cachedData]);

  // Process data when component mounts or when passedData changes
  useEffect(() => {
    const dataToUse = liveLockersData ?? passedData ?? cachedData ?? [];
    const referenceLocation =
      currentLocationLat !== undefined && currentLocationLng !== undefined
        ? { lat: currentLocationLat, lng: currentLocationLng }
        : null;
    const nextLocations = prepareLockerLocations(dataToUse, referenceLocation);

    setValidData(nextLocations);
    setFilteredLocations(nextLocations);

    if (selectedLocationKey) {
      const updatedSelectedLocation = nextLocations.find(
        (locationItem) => getLockerSelectionKey(locationItem) === selectedLocationKey
      );
      setSelectedLocation(updatedSelectedLocation || null);
    }

    setDataLoaded(true);
  }, [
    passedData,
    liveLockersData,
    cachedData,
    timestamp,
    currentLocationLat,
    currentLocationLng,
    selectedLocationKey,
  ]);

  // Update map center when center changes
  useEffect(() => {
    if (center) {
      setMapCenter(center);
      setCurrentLocation(center);
    }
  }, [center]);

  // Scroll to selected location in slider
  useEffect(() => {
    if (selectedLocation && sliderRef.current && isSliderVisible) {
      const selectedKey = getLockerSelectionKey(selectedLocation);
      const index = filteredLocations.findIndex(
        (locationItem) => getLockerSelectionKey(locationItem) === selectedKey
      );
      if (index !== -1) {
        const cardWidth = 296;
        sliderRef.current.scrollTo({
          left: index * cardWidth,
          behavior: 'smooth'
        });
      }
    }
  }, [selectedLocation, filteredLocations, isSliderVisible]);

  const getCurrentLocation = () => {
    setIsLocating(true);
    setLocationError(null);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCurrentLocation(userLocation);
          setMapCenter(userLocation);
          setZoom(16);
          setIsLocating(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError(t('msg_error_unable_to_get_location'));
          setIsLocating(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else {
      setLocationError(t('error_geolocation_not_supported_this_browser'));
      setIsLocating(false);
    }
  };

  const mapStyles = [
    { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "poi.business", stylers: [{ visibility: "off" }] },
    { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "road.local", elementType: "labels", stylers: [{ visibility: "off" }] }
  ];

  const mapOptions = {
    mapTypeId: 'roadmap',
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: false,
    gestureHandling: 'greedy',
    disableDefaultUI: true,
    styles: mapStyles,
  };

  const handleZoomIn = () => {
    const currentZoom = mapRef.current?.getZoom?.() ?? zoom;
    const nextZoom = Math.min(21, (currentZoom ?? 15) + 1);
    setZoom(nextZoom);
    if (mapRef.current?.setZoom) mapRef.current.setZoom(nextZoom);
  };

  const handleZoomOut = () => {
    const currentZoom = mapRef.current?.getZoom?.() ?? zoom;
    const nextZoom = Math.max(3, (currentZoom ?? 15) - 1);
    setZoom(nextZoom);
    if (mapRef.current?.setZoom) mapRef.current.setZoom(nextZoom);
  };

  const handleLocationSelect = (location) => {
    if (!location) return;

    setSelectedLocation(location);

    if (location.position) {
      setMapCenter(location.position);
      setZoom(17);
      setShowInfoPanel(true);
      setIsSliderVisible(true);
      setSliderHeight(35);
    }
  };

  const handleMapMarkerClick = (location) => {
    if (!location || !location.position) return;
    handleLocationSelect(location);
  };

  const getSettingsValidateAndRedirectUser = async (lockerBankId, location) => {
    try {
      const response = await getTenantAndAssignmentSettings(lockerBankId);
      if (!response || response.status !== SUCCESS) {
        console.error("Invalid response from API");
        return;
      }
      const tempResponse = response?.data?.locker_bank;
      const lockerBankAddress = buildAddress(response?.data?.locker_bank);
      storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LOCKER_BANK_ADDRESS, lockerBankAddress);

      const templockerData = {
        ...location,
        price: getSSize24HrPrice(tempResponse?.pricing) || '',
        logoImage: tempResponse?.logo_image || null,
        backgroundImage: tempResponse?.background_image || null,
        description: tempResponse?.description || '',
        address: lockerBankAddress,
        state: tempResponse?.state || '',
        lockerImages: tempResponse?.locker_images || [],
        LockerBankURL: tempResponse?.location_url || '',
        LockerBankType: tempResponse?.bank_type || '',
        
      };

      applyBackendLanguageConfig(response);
      const assignments = response?.data?.locker_assignments || [];
      const lokcerCurrency = response?.data?.locker_assignments[0]?.currency || '';
      
      if (assignments.length > 1) {
        setIsLoading(false);
        history.push({
          pathname: "/assignments",
          state: {
            tenantAndAssignmentSettings: response.data
          }
        });
        return;
      }

      if (assignments.length === 1) {
        const isAssignmentProperlyConfigured = await checkIfAssignmentIsProperlyConfigured(response.data);
        if (isAssignmentProperlyConfigured === true) {
          const lockerslug = response?.data?.locker_bank?.slug;
          storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LOCKER_SLUG, lockerslug);
          setIsLoading(false);
          history.push({
            pathname: getPathForNextPage(MAP, LOCKER_DETAILS),
            state: {
              lockerDetails: { ...templockerData, currency: lokcerCurrency },
            }
          });
        } else {
          setIsLoading(false);
          console.error("Assignment not properly configured:", isAssignmentProperlyConfigured);
        }
        return;
      }

      console.error("No locker assignments found");
    } catch (error) {
      setIsLoading(false);
      console.error("Error while getting settings and validation:", error);
    }
  };

  const handleSliderItemClick = (location) => {
    handleLocationSelect(location);
  };

  const handleSliderNextStepClick = async (location) => {
    if (!location || getLockerSelectionKey(selectedLocation) !== getLockerSelectionKey(location)) return;
        setIsLoading(true);
        SetLoadingMessage(t('pleaseWaitText'));
          const lockerBankId = location?.lockerBankId;
          const domain = location?.domain;
          const domainAndLockerBankInfoObject = {
            domain,
            lockerBankId
          };
          setDomainAndLockerBankIdInLocalStorage(domainAndLockerBankInfoObject);
          if (lockerBankId) {
            await getSettingsValidateAndRedirectUser(lockerBankId, location);
          } else {
            setIsLoading(false);
            console.error("locker bank id is not there please contact to admin");
          }
  };

  const scrollSlider = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 296;
      const newScrollLeft = sliderRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      sliderRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    setDragStartY(e.touches ? e.touches[0].clientY : e.clientY);
    setIsDragging(true);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = currentY - dragStartY;
    
    if (isSliderVisible) {
      const newHeight = Math.max(10, Math.min(35, sliderHeight - (deltaY / 10)));
      setSliderHeight(newHeight);
      
      if (newHeight < 12) {
        setIsSliderVisible(false);
      }
    } else {
      if (deltaY < -20) {
        setIsSliderVisible(true);
        setSliderHeight(35);
      }
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    
    if (isSliderVisible && sliderHeight < 12) {
      setIsSliderVisible(false);
    }
    
    if (isSliderVisible) {
      if (sliderHeight < 20) {
        setSliderHeight(15);
      } else if (sliderHeight > 35) {
        setSliderHeight(35);
      }
    }
  };

  const LockerMarker = ({ location, onClick, isSelected }) => {
    if (!location || !location.position) return null;
    
    const getSizeClass = () => {
      if (zoom >= 16) return 'xl';
      if (zoom >= 14) return 'lg';
      if (zoom >= 12) return 'md';
      if (zoom >= 10) return 'sm';
      return 'xs';
    };

    const sizeClass = getSizeClass();
    
    const getColorClass = (type) => {
      const colorMap = {
        'city': 'city',
        'industrial': 'industrial',
        'commercial': 'commercial',
        'municipal': 'municipal',
        'temple': 'temple',
        'tourist': 'tourist',
        'landmark': 'landmark',
        'area': 'area',
        'society': 'society',
        'store': 'store',
        'colony': 'colony',
        'market': 'market',
        'park': 'park'
      };
      return colorMap[type?.toLowerCase()] || 'default';
    };

    return (
      <OverlayView
        position={location.position}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      >
        <div 
          className={`marker-container ${isSelected ? 'selected' : ''}`}
          onClick={() => onClick(location)}
        >
          <div className={`marker-image-wrapper size-${sizeClass}`}>
            <img 
              src={LockerIcon}
              alt={location.name}
              className="marker-image"
            />
            <div className={`marker-badge badge-${sizeClass} marker-badge-${getColorClass(location.type)}`}>
              {location.lockerNumber?.split('-')[1] || location.id}
            </div>
          </div>
          <div className={`marker-label label-${sizeClass}`}>
            {location.distance || '0'}km
          </div>
        </div>
      </OverlayView>
    );
  };

  const CurrentLocationMarker = () => {
    const [mapsLoaded, setMapsLoaded] = useState(false);

    useEffect(() => {
      const checkMapsLoaded = () => {
        if (window.google && window.google.maps) {
          setMapsLoaded(true);
        }
      };

      checkMapsLoaded();
      const timer = setTimeout(checkMapsLoaded, 1000);
      return () => clearTimeout(timer);
    }, []);

    if (!currentLocation || !currentLocation.lat || !currentLocation.lng || !mapsLoaded) {
      return null;
    }

    return (
      <Marker
        position={currentLocation}
        icon={{
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: "#4285F4",
          fillOpacity: 1,
          strokeColor: "white",
          strokeWeight: 3,
        }}
        title="You are here"
      />
    );
  };


  return (
    <>
    <div 
      className="map-mobile-container"
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
      onMouseMove={isDragging ? handleDragMove : null}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      ref={containerRef}
    >
      <div className="map-wrapper">
        <SearchSection
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearchClear={() => {
            setSearchQuery('');
            setPlaceSuggestions([]);
            setFilteredLocations(validData);
          }}
          onSuggestionSelect={handlePlaceSuggestionSelect}
          placeholder={t('text_seach_places')}
          externalSuggestions={placeSuggestions}
          useExternalSuggestions
          validData={validData}
          currentLocation={currentLocation}
          filteredLocations={filteredLocations}
          setFilteredLocations={setFilteredLocations}
        />

        {locationError && (
          <div className="location-error">
            {locationError}
          </div>
        )}
        
        {loadError ? (
          <div className="loading-container">
            <div className="loading-content">
              <div className="loading-text">{t('info_Message_unableToLoad_map')}</div>
            </div>
          </div>
        ) : !isMapLoaded ? (
          <div className="loading-container">
            <div className="loading-content">
              <div className="loading-text">{t('loading_map_text')}</div>
            </div>
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={zoom}
            options={mapOptions}
            onClick={() => setShowInfoPanel(false)}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            onZoomChanged={() => {
              if (mapRef.current) {
                setZoom(mapRef.current.getZoom());
              }
            }}
          >
            <CurrentLocationMarker />
            
            {filteredLocations.map((location) => (
              <LockerMarker
                key={getLockerSelectionKey(location)}
                location={location}
                onClick={handleMapMarkerClick}
                isSelected={getLockerSelectionKey(selectedLocation) === getLockerSelectionKey(location)}
              />
            ))}
          </GoogleMap>
        )}
        <div 
          className={`bottom-slider-container ${!isSliderVisible ? 'hidden' : ''}`}
        >
          <div 
            className="drag-handle"
            onTouchStart={handleDragStart}
            onMouseDown={handleDragStart}
          >
            <div className="drag-handle-bar"></div>
            <div className="drag-instruction">
              {isSliderVisible 
                ? sliderHeight >= 35
                  ?  t('drag_down_to_hide_text')
                  : t('drag_down_hide_drag_up_expand_text')
                : t('drag_up_to_show_lockers_text')}
            </div>
          </div>
          
          <LocationSlider
            ref={sliderRef}
            currentLocation={currentLocation}
            filteredLocations={filteredLocations}
            selectedLocation={selectedLocation}
            onLocationClick={handleSliderItemClick}
            onNextStepClick={handleSliderNextStepClick}
            onScrollLeft={() => scrollSlider('left')}
            onScrollRight={() => scrollSlider('right')}
            isVisible={isSliderVisible}
            isLoading={isLoading}
          />
        </div>
        
        {isLoading ? 
          <CircularProgressLoader message={loadingMessage} /> : ''}

        {!isPreview && (
        <div className="map-controls" aria-label="Map controls">
          <button
            type="button"
            className={`map-control-btn map-geolocate-btn ${isLocating ? 'is-locating' : ''}`}
            onClick={getCurrentLocation}
            disabled={isLocating}
            aria-label="Go to current location"
            title={t('text_Current_Location')}
          >
            <MyLocationIcon />
          </button>
          <div className="map-zoom-controls" aria-label="Zoom controls">
            <button
              type="button"
              className="map-control-btn map-zoom-btn"
              onClick={handleZoomIn}
              aria-label="Zoom in"
              title={t('text_Zoom_In')}
            >
              +
            </button>
            <button
              type="button"
              className="map-control-btn map-zoom-btn"
              onClick={handleZoomOut}
              aria-label="Zoom out"
              title={t('text_Zoom_Out')}
            >
              −
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
    </>
  );
};



export default LockerMapMobile;
